import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles,
  Download,
  ShieldCheck,
  CheckCircle,
  HeartHandshake,
  BookOpen,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';

// Content toggles (per Updates0119.pdf)
const SHOW_TIMELINE_SECTION = false;
const SHOW_CARRY_DESKTOP_MAGIC_SECTION = false;
const SHOW_MISSION_SECTION = false;

const valuePillars = [
  {
    title: 'Desktop-Grade Experience',
    description:
      'Inspired by the MyStoryMe PWA desktop canvas, every interaction feels cinematic with floating gradients, immersive cards, and buttery-smooth motion.',
    icon: <Sparkles className="h-6 w-6 text-purple-600" />,
  },
  {
    title: 'Progressive Web App DNA',
    description:
      'Install the experience, enjoy offline reading, and sync every bedtime favorite instantly—exactly like the PWA prototype you saw in MyStoryMe_PWA_Desktop.html.',
    icon: <Download className="h-6 w-6 text-pink-600" />,
  },
  {
    title: 'Enterprise-Grade Privacy',
    description:
      'Shielded by zero-data selling policies, encrypted sync, and parent-first controls so every magical moment stays in the family.',
    icon: <ShieldCheck className="h-6 w-6 text-emerald-600" />,
  },
];

const timeline = [
  {
    year: '2019',
    title: 'A bedtime spark',
    detail: 'We started as a simple idea to turn bedtime routines into immersive story rituals.',
  },
  {
    year: '2021',
    title: 'PWA desktop breakthrough',
    detail: 'The experience blueprint from MyStoryMe_PWA_Desktop.html shaped our multi-device UI system.',
  },
  {
    year: '2023',
    title: 'Story Intelligence',
    detail: 'We launched adaptive narratives that respond to reading pace, vibes, and family feedback.',
  },
  {
    year: 'Today',
    title: 'Global imagination grid',
    detail: 'Communities in 32+ countries remix prompts, illustrations, and keepsakes together.',
  },
];

const globalHighlights = [
  {
    title: 'Build Confidence',
    description: 'Seeing themselves as a hero builds real-world courage and self-belief.',
    emoji: '✨',
  },
  {
    title: 'Foster a Love of Reading',
    description: 'Children who connect personally with books read more and retain more.',
    emoji: '📚',
  },
  {
    title: 'Supercharge Creativity',
    description: 'Every story becomes a springboard for imagination — sparking play, curiosity, and big dreams.',
    emoji: '🎨',
  },
  {
    title: 'Make Reading Fun Again',
    description: 'No more yawns at bedtime — these stories captivate and delight.',
    emoji: '😄',
  },
];

const About = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const handleCreate = () => navigate(isAuthenticated ? '/profiles/create' : '/register');

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-yellow-50 flex flex-col">
      <Header />
      <main className="flex-1">
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="w-52 h-52 bg-purple-200 rounded-full blur-3xl opacity-50 absolute -top-12 -left-6" />
            <div className="w-40 h-40 bg-pink-200 rounded-full blur-3xl opacity-60 absolute top-10 right-6" />
            <div className="w-48 h-48 bg-orange-200 rounded-full blur-3xl opacity-40 absolute bottom-10 left-1/3" />
          </div>
          <div className="container mx-auto px-4 py-16 lg:py-24 relative">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 border border-purple-100 text-purple-700 font-semibold text-sm mb-6">
                  <BookOpen className="h-4 w-4" />
                  About mySTORYme
                </div>
                <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
                  Where Every Child Becomes the{' '}
                  <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                    Hero
                  </span>{' '}
                  of Their Own Story
                </h1>
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  At mySTORYme, we’re not just making stories — we’re shaping the way children see themselves in the world.
                  We exist to transform everyday moments into unforgettable adventures where your child isn’t a background
                  character… they are the star, the hero, the explorer, the dreamer.
                </p>
                <div className="flex flex-wrap gap-4">
                  <button
                    type="button"
                    onClick={handleCreate}
                    className="px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold shadow-lg hover:shadow-xl transition-transform hover:-translate-y-0.5"
                  >
                    Start Your Child’s Story Now
                  </button>
                </div>
              </div>
              <div className="relative">
                <div className="absolute -top-6 -right-6 w-28 h-28 rounded-3xl bg-gradient-to-br from-purple-200 to-pink-200 opacity-60 blur-2xl" />
                <div className="bg-white rounded-[2rem] shadow-2xl border border-purple-100 p-6 relative">
                  <div className="flex items-center justify-between mb-4">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-50 text-purple-700 font-semibold text-xs border border-purple-100">
                      <Sparkles className="h-4 w-4" />
                      Our Mission
                    </div>
                    <span className="px-3 py-1 bg-green-50 text-green-700 text-xs font-semibold rounded-full">
                      Glow mode
                    </span>
                  </div>

                  <h3 className="text-2xl font-extrabold text-gray-900 mb-2">Our Mission</h3>
                  <p className="text-sm text-gray-600 leading-relaxed mb-4">
                    Kids deserve more than generic bedtime books. They deserve stories that:
                  </p>

                  <div className="rounded-2xl border border-purple-100 bg-gradient-to-br from-white to-purple-50 p-4 shadow-sm">
                    <ul className="space-y-3 text-sm text-gray-700">
                      <li className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                        <span>Reflect who they are — not who someone else imagines them to be.</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                        <span>Fuel imagination and confidence by placing them at the center of the narrative.</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                        <span>Turn reading time into a moment children look forward to — not just tolerate.</span>
                      </li>
                    </ul>
                  </div>

                  <p className="text-sm text-gray-600 leading-relaxed mt-4">
                    At mySTORYme, every story is powered by your child’s name, interests, loved ones, and personal details —
                    making it truly theirs from page one to the last chapter.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {SHOW_MISSION_SECTION && (
        <section className="bg-gradient-to-br from-white/80 via-purple-50/60 to-pink-50/70 backdrop-blur-sm border-y border-purple-100/60">
          <div className="container mx-auto px-4 py-16">
            <div className="relative overflow-hidden rounded-3xl border border-purple-100 bg-white/70 shadow-2xl">
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute -top-16 -left-16 h-56 w-56 rounded-full bg-purple-200/50 blur-3xl" />
                <div className="absolute -bottom-20 -right-16 h-64 w-64 rounded-full bg-pink-200/50 blur-3xl" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_#ffffff70,_transparent_55%)] opacity-70" />
              </div>

              <div className="relative p-8 md:p-10 lg:p-12">
                <div className="text-center mb-10">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 border border-purple-100 text-purple-700 font-semibold text-sm shadow-sm">
                    <Sparkles className="h-4 w-4" />
                    Our Mission
                  </div>
                  <h2 className="text-3xl lg:text-5xl font-extrabold text-gray-900 mt-5">Our Mission</h2>
                  <p className="text-lg text-gray-600 mt-3 max-w-3xl mx-auto">
                    Kids deserve more than generic bedtime books. They deserve stories that:
                  </p>
                </div>

                <div className="grid md:grid-cols-3 gap-6 mb-10">
                  {valuePillars.map((pillar) => (
                    <div
                      key={pillar.title}
                      className="group rounded-2xl bg-white/90 border border-purple-100/70 p-6 shadow-lg hover:shadow-xl transition-all duration-200 hover:-translate-y-0.5 h-full"
                    >
                      <div className="mb-4 inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-100 shadow-sm">
                        {pillar.icon}
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{pillar.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{pillar.description}</p>
                    </div>
                  ))}
                </div>

                <div className="max-w-3xl mx-auto">
                  <div className="rounded-2xl border border-purple-100 bg-white/80 p-6 shadow-sm">
                    <ul className="space-y-3 text-gray-700">
                      <li className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                        <span>Reflect who they are — not who someone else imagines them to be.</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                        <span>Fuel imagination and confidence by placing them at the center of the narrative.</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                        <span>Turn reading time into a moment children look forward to — not just tolerate.</span>
                      </li>
                    </ul>
                  </div>

                  <p className="text-gray-600 leading-relaxed mt-6 text-center">
                    At mySTORYme, every story is powered by your child’s name, interests, loved ones, and personal details —
                    making it truly theirs from page one to the last chapter.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
        )}

        <section className="container mx-auto px-4 py-16">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 border border-purple-100 text-purple-700 font-semibold text-sm shadow-sm">
                <HeartHandshake className="h-4 w-4" />
                What We Do
              </div>
              <h2 className="text-3xl lg:text-5xl font-extrabold text-gray-900 mt-5 mb-3">What We Do</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                We’ve built a simple, magical platform where parents:
              </p>
            </div>

            <div className="relative overflow-hidden rounded-3xl border border-purple-100 bg-white/80 shadow-2xl">
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute -top-14 -right-10 h-56 w-56 rounded-full bg-pink-200/40 blur-3xl" />
                <div className="absolute -bottom-16 -left-12 h-64 w-64 rounded-full bg-purple-200/40 blur-3xl" />
              </div>

              <div className="relative p-8 md:p-10">
                <div className="grid gap-6 md:grid-cols-3">
                  <div className="rounded-2xl bg-white/90 border border-purple-100/70 p-6 shadow-lg">
                    <div className="flex items-start gap-4">
                      <div className="w-11 h-11 min-w-[44px] min-h-[44px] aspect-square rounded-full bg-gradient-to-r from-purple-600 to-pink-600 text-white font-extrabold flex items-center justify-center shadow-lg border-4 border-white/90 shrink-0 leading-none">
                        1
                      </div>
                      <p className="text-gray-700 leading-relaxed">Create a story profile for their child.</p>
                    </div>
                  </div>
                  <div className="rounded-2xl bg-white/90 border border-purple-100/70 p-6 shadow-lg">
                    <div className="flex items-start gap-4">
                      <div className="w-11 h-11 min-w-[44px] min-h-[44px] aspect-square rounded-full bg-gradient-to-r from-purple-600 to-pink-600 text-white font-extrabold flex items-center justify-center shadow-lg border-4 border-white/90 shrink-0 leading-none">
                        2
                      </div>
                      <p className="text-gray-700 leading-relaxed">Fill in personal details — favorites, dreams, personality quirks.</p>
                    </div>
                  </div>
                  <div className="rounded-2xl bg-white/90 border border-purple-100/70 p-6 shadow-lg">
                    <div className="flex items-start gap-4">
                      <div className="w-11 h-11 min-w-[44px] min-h-[44px] aspect-square rounded-full bg-gradient-to-r from-purple-600 to-pink-600 text-white font-extrabold flex items-center justify-center shadow-lg border-4 border-white/90 shrink-0 leading-none">
                        3
                      </div>
                      <p className="text-gray-700 leading-relaxed">
                        Instantly generate enchanting stories where the child leads every adventure.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 rounded-2xl border border-purple-100 bg-gradient-to-br from-white to-purple-50 p-6 shadow-sm">
                  <p className="text-gray-600 leading-relaxed">
                    From space voyages to forest mysteries, treasure hunts to enchanted kingdoms, every narrative is uniquely tailored
                    to reflect your child’s inner world.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* <section className="bg-white/80 backdrop-blur-sm">
          <div className="container mx-auto px-4 py-16">
            <div className="max-w-4xl mx-auto">
              <div className="flex justify-center mb-4">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 border border-purple-100 text-purple-700 font-semibold text-sm shadow-sm text-center">
                  <HeartHandshake className="h-4 w-4" />
                  Why It Matters
                </div>
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4 text-center">Why It Matters</h2>
              <p className="text-lg text-gray-600 text-center mb-10">
                Kids are full of potential — and stories are one of the most powerful tools we have to:
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white rounded-2xl border border-purple-100 shadow-lg p-6">
                  <p className="text-lg font-semibold text-gray-900 mb-2">✨ Build Confidence</p>
                  <p className="text-gray-600">Seeing themselves as a hero builds real-world courage and self-belief.</p>
                </div>
                <div className="bg-white rounded-2xl border border-purple-100 shadow-lg p-6">
                  <p className="text-lg font-semibold text-gray-900 mb-2">📚 Foster a Love of Reading</p>
                  <p className="text-gray-600">Children who connect personally with books read more and retain more.</p>
                </div>
                <div className="bg-white rounded-2xl border border-purple-100 shadow-lg p-6">
                  <p className="text-lg font-semibold text-gray-900 mb-2">🎨 Supercharge Creativity</p>
                  <p className="text-gray-600">Every story becomes a springboard for imagination — sparking play, curiosity, and big dreams.</p>
                </div>
                <div className="bg-white rounded-2xl border border-purple-100 shadow-lg p-6">
                  <p className="text-lg font-semibold text-gray-900 mb-2">😄 Make Reading Fun Again</p>
                  <p className="text-gray-600">No more yawns at bedtime — these stories captivate and delight.</p>
                </div>
              </div>
            </div>
          </div>
        </section> */}

        <section className="container mx-auto px-4 py-16">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-purple-50 text-purple-700 font-semibold">
              <HeartHandshake className="h-4 w-4" />
              Why It Matters
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mt-6 mb-4">Why It Matters</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Kids are full of potential — and stories are one of the most powerful tools we have to:
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {globalHighlights.map((card) => (
              <div key={card.title} className="rounded-2xl border border-purple-100 bg-white p-5 shadow-lg space-y-3">
                <div className="w-10 h-10 rounded-2xl bg-purple-50 flex items-center justify-center">
                  <span className="text-xl" aria-hidden="true">
                    {card.emoji}
                  </span>
                </div>
                <h3 className="font-semibold text-gray-900">{card.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{card.description}</p>
              </div>
            ))}
          </div>
        </section>

        {SHOW_TIMELINE_SECTION && (
        <section className="bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 text-white">
          <div className="container mx-auto px-4 py-16">
            <div className="grid md:grid-cols-4 gap-8">
              {timeline.map((item) => (
                <div key={item.year} className="relative pl-6">
                  <div className="absolute left-0 top-1 w-3 h-3 rounded-full bg-gradient-to-r from-pink-400 to-yellow-300" />
                  <p className="text-sm uppercase tracking-widest text-white/70">{item.year}</p>
                  <h4 className="text-xl font-semibold mt-2">{item.title}</h4>
                  <p className="text-white/80 mt-2 text-sm leading-relaxed">{item.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
        )}

        {SHOW_CARRY_DESKTOP_MAGIC_SECTION && (
        <section className="container mx-auto px-4 py-14">
          <div className="bg-white rounded-3xl border border-purple-100 shadow-2xl p-8 md:p-12 text-center">
            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-purple-50 text-purple-600 font-semibold mb-6">
              <Sparkles className="h-4 w-4" />
              Ready when your child says “one more story!”
            </div>
            <h3 className="text-3xl font-bold text-gray-900 mb-4">Carry the desktop magic everywhere</h3>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Install the PWA, save favorites offline, and remix characters with the same playful gradients and floating
              elements you loved in the prototype.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button
                type="button"
                onClick={handleCreate}
                className="px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold shadow-lg hover:shadow-xl transition-transform hover:-translate-y-0.5"
              >
                Create a profile
              </button>
            </div>
          </div>
        </section>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default About;


